import { useContext, useMemo } from 'react';
import { UserContext, SystemFeaturesContext, ModuleBase, GeneralImage, Tooltip } from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel, TechLaunchDatabaseModel, ScrapbookContentType } from '@/types';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { SaveToScrapbookButton } from '@/components/scrapbook';

type TechLaunchDatabaseEntryModuleModel = ModuleBaseModel<
  {
    data: TechLaunchDatabaseModel;
  } & ModuleBasePropsModel
>;

const TechLaunchDatabaseEntryModule = (moduleData: TechLaunchDatabaseEntryModuleModel) => {
  const {
    props: { data: _data },
  } = moduleData;

  const data = useMemo(() => {
    return {
      ..._data,
      innovationImages: _data.innovationImages?.imageUrl
        ? _data.innovationImages
        : {
            imageUrl: '/Images/tech_launches_database_temp.jpg',
            imageAlt: 'Default Image',
            caption: '',
            copyrightText: '',
            mediaId: 0,
          },
    };
  }, [_data]);

  const t = useTranslations();

  const user = useContext(UserContext);
  const { saveToScrapbook: systemFeaturesSaveToScrapbook } = useContext(SystemFeaturesContext);

  const dataList = useMemo(
    () => [
      { label: 'Client Vertical', value: data.clientVertical },
      // { label: 'Themes', value: data.themes },
      { label: 'Company', value: data.company },
      { label: 'Use Cases', value: data.useCases },
      { label: 'Value Chain', value: data.valueChain },
      { label: 'Date', value: data.publishedDate ? dayjs(data.publishedDate).format('MMMM YYYY') : '' },
    ],
    [data],
  );

  const download = () => {
    // TODO: implement download logic
  };

  return (
    <ModuleBase data={moduleData}>
      <div className="container">
        <h2 className="heading text-h2 color-from-bg w-7/12 pb-3 sm:w-9/12 md:pb-5">{data.title}</h2>
        {data.innovationImages && (
          <div className="aspect-[16/7]">
            <GeneralImage
              image={data.innovationImages}
              className="[&_.general-image]:w-full [&_.general-image]:cursor-default"
              widths={{ xs: 350, sm: 660, md: 660, lg: 850, xl: 930, xxl: 1500 }}
              heights={{ xs: 154, sm: 290, md: 290, lg: 370, xl: 410, xxl: 750 }}
            />
          </div>
        )}
        <div className="relative flex gap-2 pt-4 print:hidden">
          {/*
          <Tooltip placement="bottom" content={t('general.$download')}>
            <button className="cursor-pointer rounded-full p-2 hover:bg-gray-100" onClick={download}>
              <DownloadSimpleLight className="fill-from-bg h-7 w-7" />
            </button>
          </Tooltip>
          */}
          {!user.isIpBasedIdentity && systemFeaturesSaveToScrapbook && (
            <div className="flex justify-end">
              <SaveToScrapbookButton
                scrapbookContent={data}
                scrapbookContentType={ScrapbookContentType.TechLaunchDatabase}
                scrapbookPath={`tech-launch-database.${data.innovationId}.json`}
                successMessage="Tech launch saved to scrapbook successfully"
                tooltipProps={{ placement: 'bottom', content: 'Save to Scrapbook' }}
                className="btn icon btn-from-bg transparent hover:!bg-gray-100 [&_svg]:h-7 [&_svg]:w-7"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col pt-6">
          {dataList.map(
            (item, index) =>
              item.value && (
                <div className="flex border-x border-t border-[#cfcfcf] last:border-b" key={index}>
                  <div className="w-4/12 border-r border-[#cfcfcf] bg-[#f1f1f1] p-2 md:w-3/12 md:p-4">{item.label}</div>
                  <div className="w-8/12 p-2 md:w-9/12 md:p-4">{item.value}</div>
                </div>
              ),
          )}
        </div>
        {data.description && (
          <div
            className="richText py-6 print:break-before-page"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        )}
      </div>
    </ModuleBase>
  );
};

export default TechLaunchDatabaseEntryModule;
